import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { MoreVertical, MapPin, Cpu, Plus, X } from "lucide-react";
import toast from "react-hot-toast";
import { DsBadge, DsButton, DsSpinner } from "../../../../../design-system";
import { attendanceService } from "../../../../../services/api";
import {
  ATTENDANCE_PUNCH_UPDATED_EVENT,
  hydrateEmployeeWorklogFromAttendance,
} from "../../../../../lib/attendancePunchStorage";
import {
  describeBiometricDevice,
  isOnlinePunchSource,
  mapsUrlForCoords,
  reverseGeocodeAddress,
} from "../../../../../lib/punchLocation";
import {
  PHONE_SHEET_BACKDROP_CLS,
  PHONE_SHEET_PANEL_CLS,
  usePhoneFormBack,
} from "../RequestHistoryOverlay";

const ATTENDANCE_TIMEZONE = "Asia/Kolkata";

function employeeLabel(employee) {
  if (!employee) return "Employee";
  const code = employee.empCode || employee.employeeCode || employee.code || "";
  const name =
    employee.user?.name ||
    employee.employeeName ||
    employee.name ||
    employee.fullName ||
    [employee.firstName, employee.lastName].filter(Boolean).join(" ").trim();
  if (code && name) return `${code} — ${name}`;
  return name || code || "Employee";
}

function todayIso() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: ATTENDANCE_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const values = {};

  for (const part of parts) {
    if (part.type !== "literal") {
      values[part.type] = part.value;
    }
  }

  return `${values.year}-${values.month}-${values.day}`;
}

function formatPunchClock(iso) {
  if (!iso) return "—";

  const d = new Date(iso);

  if (Number.isNaN(d.getTime())) {
    return String(iso);
  }

  return d.toLocaleTimeString("en-IN", {
    timeZone: ATTENDANCE_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

function toDatetimeLocalValue(iso) {
  if (!iso) return "";

  const d = new Date(iso);

  if (Number.isNaN(d.getTime())) {
    return "";
  }

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: ATTENDANCE_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(d);

  const values = {};

  for (const part of parts) {
    if (part.type !== "literal") {
      values[part.type] = part.value;
    }
  }

  return `${values.year}-${values.month}-${values.day}T${values.hour}:${values.minute}`;
}

function sourceTone(source) {
  const s = String(source || "").toLowerCase();
  if (s === "manual") return "info";
  if (s === "biometric" || s === "device") return "warning";
  if (s === "regularise") return "success";
  if (s === "mobile") return "info";
  return "neutral";
}

function PunchLocationOrDevice({ punch }) {
  const source = String(punch?.source || "").toLowerCase();
  const storedAddress = String(punch?.locationAddress || punch?.address || "").trim();
  const [resolvedAddress, setResolvedAddress] = useState(storedAddress);
  const mapHref = mapsUrlForCoords(punch?.latitude, punch?.longitude);

  useEffect(() => {
    setResolvedAddress(storedAddress);
    if (storedAddress || !mapHref) return undefined;
    let cancelled = false;
    reverseGeocodeAddress(punch.latitude, punch.longitude).then((name) => {
      if (!cancelled && name) setResolvedAddress(name);
    });
    return () => {
      cancelled = true;
    };
  }, [storedAddress, mapHref, punch?.latitude, punch?.longitude]);

  if (source === "biometric" || source === "device") {
    const info = describeBiometricDevice(punch?.deviceId);
    return (
      <span
        className="inline-flex max-w-[11rem] items-center gap-1 truncate text-[11px] text-[hsl(var(--ds-muted-fg))]"
        title={info?.detail || punch?.deviceId || "Biometric device"}
      >
        <Cpu className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{info?.label || punch?.deviceId || "Device"}</span>
      </span>
    );
  }
  if (!isOnlinePunchSource(source)) {
    return null;
  }

  const tooltip = resolvedAddress || (mapHref ? "Resolving address…" : "Location not captured");

  if (mapHref) {
    return (
      <a
        href={mapHref}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex rounded-md p-1.5 text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-muted))]"
        title={tooltip}
        aria-label={tooltip}
        onClick={(e) => e.stopPropagation()}
      >
        <MapPin className="h-4 w-4" />
      </a>
    );
  }

  return (
    <span
      className="inline-flex rounded-md p-1.5 text-[hsl(var(--ds-muted-fg))]"
      title={tooltip}
      aria-label={tooltip}
    >
      <MapPin className="h-4 w-4 opacity-45" />
    </span>
  );
}



async function runMutate(fn, { onCompOffApproved }) {
  try {
    return await fn(false);
  } catch (err) {
    const code = err?.response?.data?.code || err?.code;
    const message = err?.response?.data?.message || err?.message || "Request failed";
    const compOff = err?.response?.data?.compOff;
    if (code === "COMP_OFF_APPLICATION") {
      toast.error(message);
      throw err;
    }
    if (code === "COMP_OFF_APPROVED") {
      const ok =
        typeof onCompOffApproved === "function"
          ? await onCompOffApproved({ message, compOff })
          : window.confirm(
              `${message}\n\nCancel earned/availed Comp Off for this day and continue?`
            );
      if (!ok) throw err;
      return fn(true);
    }
    if (code === "MONTH_LOCKED" || code === "DAY_LOCKED") {
      toast.error(message);
      throw err;
    }
    toast.error(message);
    throw err;
  }
}

export default function ViewEditChecksPopup({
  darkMode = false,
  isOpen,
  onClose,
  employee,
  canCreate = true,
  canEdit = true,
  canDelete = true,
  readOnly = false,
  initialDate = null,
  lockDate = false,
}) {
  const [selectedDate, setSelectedDate] = useState(initialDate || todayIso());
  const [day, setDay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [menuKey, setMenuKey] = useState(null);
  const [editPunch, setEditPunch] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addKind, setAddKind] = useState("in");
  const [addAt, setAddAt] = useState("");
  const [editKind, setEditKind] = useState("in");
  const [editAt, setEditAt] = useState("");

  const punches = useMemo(() => day?.punches || [], [day]);
  const allowCreate = canCreate && !readOnly;
  const allowEdit = canEdit && !readOnly;
  const allowDelete = canDelete && !readOnly;

  const load = useCallback(async () => {
    if (!isOpen || !employee?.id || !selectedDate) return;
    setLoading(true);
    try {
      const res = await attendanceService.listEmployeeChecks({
        employeeId: employee.id,
        fromDate: selectedDate,
        toDate: selectedDate,
      });
      const list = Array.isArray(res?.data?.days) ? res.data.days : [];
      const match =
        list.find((d) => String(d.date).slice(0, 10) === selectedDate) || null;
      const next = match || {
        date: selectedDate,
        status: "-",
        checkIn: null,
        checkOut: null,
        totalHours: null,
        openSession: false,
        isLocked: false,
        punches: [],
      };
      setDay(next);
      if (match) {
        hydrateEmployeeWorklogFromAttendance(employee.id, match);
      }
    } catch (err) {
      console.error(err);
      setDay({
        date: selectedDate,
        status: "-",
        punches: [],
        isLocked: false,
      });
      toast.error(err?.response?.data?.message || err?.message || "Failed to load checks");
    } finally {
      setLoading(false);
    }
  }, [isOpen, employee?.id, selectedDate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!isOpen) {
      setMenuKey(null);
      setEditPunch(null);
      setAddOpen(false);
      setSelectedDate(initialDate || todayIso());
    } else if (initialDate) {
      setSelectedDate(initialDate);
    }
  }, [isOpen, initialDate]);

  const confirmCancelCompOff = async ({ message }) =>
    window.confirm(
      `${message}\n\nThis will cancel earned and availed Comp Off for that day, then apply your check change.`
    );

  const afterMutate = async (result) => {
    await load();
    const att = result?.attendance;
    if (att && employee?.id) {
      hydrateEmployeeWorklogFromAttendance(
        employee.id,
      {
        date: String(att.date || "").slice(0, 10),
        checkIn: att.checkIn,
        checkOut: att.checkOut,
        punches: result.punches || [],
        status: att.status,
      });
    }
    if (result?.compOffCancelled?.length) {
      toast.success("Comp Off cancelled for this day; checks updated.");
    } else {
      toast.success("Checks updated. Attendance recalculated.");
    }
  };

  const handleDelete = async (punch) => {
    if (!allowDelete || busy || !day) return;
    const label = `${formatPunchClock(punch.punchedAt)} (${punch.punchKind})`;
    if (!window.confirm(`Delete check ${label} on ${day.date}?`)) return;
    setBusy(true);
    setMenuKey(null);
    try {
      const id = punch.id != null ? punch.id : "synthetic";
      const result = await runMutate(
        (confirmCancelApprovedCompOff) =>
          attendanceService.deleteEmployeeCheck(id, {
            employeeId: employee.id,
            syntheticId: punch.synthetic ? punch.syntheticId : null,
            attendanceDate: day.date,
            confirmCancelApprovedCompOff,
          }),
        { onCompOffApproved: confirmCancelCompOff }
      );
      await afterMutate(result?.data || result);
    } catch {
      /* toasted */
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (punch) => {
    setMenuKey(null);
    setEditPunch(punch);
    setEditKind(punch.punchKind === "out" ? "out" : "in");
    setEditAt(toDatetimeLocalValue(punch.punchedAt));
  };

  const saveEdit = async () => {
    if (!editPunch || !allowEdit || busy || !day) return;
    if (!editAt) {
      toast.error("Enter date and time");
      return;
    }
    setBusy(true);
    try {
      const id = editPunch.id != null ? editPunch.id : "synthetic";
      const result = await runMutate(
        (confirmCancelApprovedCompOff) =>
          attendanceService.updateEmployeeCheck(id, {
            employeeId: employee.id,
            syntheticId: editPunch.synthetic ? editPunch.syntheticId : null,
            attendanceDate: day.date,
            punchedAt: `${editAt}:00+05:30`,
            punchKind: editKind,
            confirmCancelApprovedCompOff,
          }),
        { onCompOffApproved: confirmCancelCompOff }
      );
      setEditPunch(null);
      await afterMutate(result?.data || result);
    } catch {
      /* toasted */
    } finally {
      setBusy(false);
    }
  };

  const saveAdd = async () => {
    if (!allowCreate || busy || !selectedDate) return;
    if (!addAt) {
      toast.error("Enter time");
      return;
    }
    setBusy(true);
    try {
      const normalizedTime =
        addAt.length === 5
          ? `${addAt}:00`
          : addAt;

      const punchedAt = `${selectedDate}T${normalizedTime}+05:30`;
      const result = await runMutate(
        (confirmCancelApprovedCompOff) =>
          attendanceService.addEmployeeCheck({
            employeeId: employee.id,
            attendanceDate: selectedDate,
            punchedAt: punchedAt,
            punchKind: addKind,
            confirmCancelApprovedCompOff,
          }),
        { onCompOffApproved: confirmCancelCompOff }
      );
      setAddOpen(false);
      setAddAt("");
      await afterMutate(result?.data || result);
    } catch {
      /* toasted */
    } finally {
      setBusy(false);
    }
  };

  usePhoneFormBack(isOpen, onClose);

  if (!isOpen || typeof document === "undefined") return null;

  const panel = darkMode
    ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
    : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]";

  return createPortal(
    <div
      className={`fixed inset-0 z-[11050] flex items-center justify-center bg-black/40 p-4 ${PHONE_SHEET_BACKDROP_CLS}`}
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose?.();
      }}
    >
      <div
        className={`flex h-[min(90vh,36rem)] w-full max-w-3xl flex-col overflow-hidden rounded-xl border shadow-[var(--ds-shadow-card)] max-md:h-[85dvh] ${PHONE_SHEET_PANEL_CLS} ${panel}`}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <div>
            <h2 className="text-base font-semibold text-[hsl(var(--ds-fg))]">
              {readOnly ? "View checks" : "View and Edit Checks"}
            </h2>
            <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">
              {employeeLabel(employee)} · web, biometric, mobile, regularise, manual
              {readOnly ? " · read only" : ""}
            </p>
          </div>
          <button
            type="button"
            className="rounded-lg p-1.5 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
            onClick={() => !busy && onClose?.()}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-[hsl(var(--ds-border))] px-4 py-2">
          <label className="text-xs text-[hsl(var(--ds-muted-fg))]">
            Date
            <input
              type="date"
              className="ml-2 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-bg))] px-2 py-1 text-xs text-[hsl(var(--ds-fg))] disabled:opacity-70"
              value={selectedDate}
              max={todayIso()}
              disabled={lockDate || readOnly}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                setSelectedDate(v);
                setAddOpen(false);
                setEditPunch(null);
                setMenuKey(null);
              }}
            />
          </label>
          {day && (
            <div className="flex flex-wrap items-center gap-2">
              <DsBadge tone="neutral">{day.status || "-"}</DsBadge>
              {day.totalHours && (
                <span className="text-xs text-[hsl(var(--ds-muted-fg))]">
                  {day.totalHours}
                </span>
              )}
              {day.openSession && <DsBadge tone="warning">Open session</DsBadge>}
              {day.isLocked && <DsBadge tone="danger">Locked</DsBadge>}
            </div>
          )}
          {allowCreate && (
            <DsButton
              type="button"
              variant="primary"
              size="sm"
              className="ml-auto"
              disabled={busy || day?.isLocked}
              onClick={() => {
                setEditPunch(null);
                setAddOpen(true);
                setAddKind("in");
                setAddAt("09:00");
              }}
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              Add check
            </DsButton>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {loading ? (
            <div className="flex justify-center py-12">
              <DsSpinner />
            </div>
          ) : punches.length === 0 ? (
            <p className="py-10 text-center text-sm text-[hsl(var(--ds-muted-fg))]">
              No checks on {selectedDate}.
              {allowCreate ? " Use Add check to create a manual punch for this day." : ""}
            </p>
          ) : (
            <ul className="divide-y divide-[hsl(var(--ds-border))] rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-bg))] px-3">
              {punches.map((punch, idx) => {
                const key = `${selectedDate}:${punch.id || punch.syntheticId}:${idx}`;
                return (
                  <li
                    key={key}
                    className="relative flex items-center gap-2 py-2.5 text-sm max-md:flex-wrap max-md:gap-y-1 max-md:py-3"
                  >
                    <span className="w-16 font-medium uppercase text-[hsl(var(--ds-fg))]">
                      {punch.punchKind}
                    </span>
                    <span className="flex-1 text-[hsl(var(--ds-fg))]">
                      {formatPunchClock(punch.punchedAt)}
                    </span>
                    <DsBadge tone={sourceTone(punch.source)}>
                      {punch.source || "web"}
                      {punch.synthetic ? " · sync" : ""}
                    </DsBadge>
                    <PunchLocationOrDevice punch={punch} />
                    {(allowEdit || allowDelete) && !day?.isLocked && (
                      <div className="relative">
                        <button
                          type="button"
                          className="rounded-md p-1.5 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                          aria-label="Punch actions"
                          onClick={() =>
                            setMenuKey((k) => (k === key ? null : key))
                          }
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                        {menuKey === key && (
                          <div className="absolute right-0 z-10 mt-1 min-w-[8rem] rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] py-1 shadow-[var(--ds-shadow-card)]">
                            {allowEdit && (
                              <button
                                type="button"
                                className="block w-full px-3 py-1.5 text-left text-xs hover:bg-[hsl(var(--ds-muted))]"
                                onClick={() => openEdit(punch)}
                              >
                                Edit
                              </button>
                            )}
                            {allowDelete && (
                              <button
                                type="button"
                                className="block w-full px-3 py-1.5 text-left text-xs text-[hsl(var(--ds-danger))] hover:bg-[hsl(var(--ds-muted))]"
                                onClick={() => handleDelete(punch)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {(addOpen || editPunch) && (
          <div className="shrink-0 border-t border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/40 px-4 py-3">
            {addOpen && (
              <div className="flex flex-wrap items-end gap-2">
                <span className="text-xs text-[hsl(var(--ds-muted-fg))]">
                  Adding for <strong className="text-[hsl(var(--ds-fg))]">{selectedDate}</strong>
                  {" · "}Source: manual
                </span>
                <label className="text-xs">
                  Kind
                  <select
                    className="mt-1 block rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 py-1 text-xs"
                    value={addKind}
                    onChange={(e) => setAddKind(e.target.value)}
                  >
                    <option value="in">IN</option>
                    <option value="out">OUT</option>
                  </select>
                </label>
                <label className="text-xs">
                  Time
                  <input
                    type="time"
                    step="1"
                    className="mt-1 block rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 py-1 text-xs"
                    value={addAt}
                    onChange={(e) => setAddAt(e.target.value)}
                  />
                </label>
                <div className="ml-auto flex gap-2">
                  <DsButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => setAddOpen(false)}
                  >
                    Cancel
                  </DsButton>
                  <DsButton
                    type="button"
                    variant="primary"
                    size="sm"
                    disabled={busy}
                    onClick={saveAdd}
                  >
                    Save check
                  </DsButton>
                </div>
              </div>
            )}
            {editPunch && (
              <div className="flex flex-wrap items-end gap-2">
                <label className="text-xs">
                  Kind
                  <select
                    className="mt-1 block rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 py-1 text-xs"
                    value={editKind}
                    onChange={(e) => setEditKind(e.target.value)}
                  >
                    <option value="in">IN</option>
                    <option value="out">OUT</option>
                  </select>
                </label>
                <label className="text-xs">
                  Date & time
                  <input
                    type="datetime-local"
                    step="1"
                    className="mt-1 block rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 py-1 text-xs"
                    value={editAt}
                    onChange={(e) => setEditAt(e.target.value)}
                  />
                </label>
                <div className="ml-auto flex gap-2">
                  <DsButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => setEditPunch(null)}
                  >
                    Cancel
                  </DsButton>
                  <DsButton
                    type="button"
                    variant="primary"
                    size="sm"
                    disabled={busy}
                    onClick={saveEdit}
                  >
                    Save changes
                  </DsButton>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
